import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import AgentAvatar from "@/components/AgentAvatar";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Loader2, Save, ShieldCheck, KeyRound, Upload } from "lucide-react";

const ALLOWED_PHOTO = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

export default function Settings() {
  const { user, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    country: user?.country || "",
  });
  const [pw, setPw] = useState({ old_password: "", new_password: "" });
  const [busy, setBusy] = useState({});

  const saveProfile = async () => {
    setBusy((b) => ({ ...b, profile: true }));
    try {
      await api.patch("/auth/profile", profile);
      await refreshMe();
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, profile: false })); }
  };

  const savePassword = async () => {
    if (pw.new_password.length < 6) { toast.error("New password must be 6+ characters"); return; }
    setBusy((b) => ({ ...b, pw: true }));
    try {
      await api.post("/auth/change-password", pw);
      setPw({ old_password: "", new_password: "" });
      toast.success("Password updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, pw: false })); }
  };

  const toggle2FA = async () => {
    setBusy((b) => ({ ...b, twofa: true }));
    try {
      await api.post("/auth/2fa/toggle", { enabled: !user?.two_fa_enabled });
      await refreshMe();
      toast.success(user?.two_fa_enabled ? "2FA disabled" : "2FA enabled — codes will be emailed to you");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, twofa: false })); }
  };

  const onLogout = async () => { await logout(); navigate("/"); };

  // Agent profile photo upload (visible only when role=agent)
  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!ALLOWED_PHOTO.includes(file.type)) {
      toast.error("Use a JPG, PNG or WEBP image");
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Image too large (max 5 MB)");
      return;
    }
    setBusy((b) => ({ ...b, photo: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: up } = await api.post("/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post("/agents/me/photo", { photo_url: up.url });
      await refreshMe();
      toast.success("Photo submitted — visible to clients once an admin approves it");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, photo: false }));
    }
  };

  const currentPhoto = user?.agent_profile?.photo_url || user?.agent_profile?.selfie_url;
  const pendingPhoto = user?.agent_profile?.photo_url_pending;
  const photoStatus = user?.agent_profile?.photo_review_status;
  const photoRejectReason = user?.agent_profile?.photo_rejected_reason;

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-10 space-y-6">
        <div>
          <span className="eb-chip">Account</span>
          <h1 className="mt-3 text-4xl">Settings</h1>
        </div>

        {/* Profile */}
        <section className="eb-card p-7">
          <h2 className="text-xl font-[800]">Profile</h2>
          <p className="text-sm text-[var(--eb-text-dim)] mt-1">{user?.email} · signed in via {user?.auth_provider}</p>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <div>
              <label className="eb-label">Full name</label>
              <input data-testid="settings-name-input" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="eb-input" />
            </div>
            <div>
              <label className="eb-label">Phone</label>
              <input data-testid="settings-phone-input" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="eb-input" />
            </div>
            <div className="sm:col-span-2">
              <label className="eb-label">Country / city</label>
              <input data-testid="settings-country-input" value={profile.country} onChange={(e) => setProfile({ ...profile, country: e.target.value })} className="eb-input" />
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button data-testid="settings-save-profile" onClick={saveProfile} disabled={busy.profile} className="eb-btn eb-btn-primary">
              {busy.profile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save profile
            </button>
          </div>
        </section>

        {/* Agent profile photo (agent self-service — public to clients) */}
        {user?.role === "agent" && (
          <section className="eb-card p-7" data-testid="settings-agent-photo">
            <h2 className="text-xl font-[800]">Profile photo</h2>
            <p className="text-sm text-[var(--eb-text-dim)] mt-1">
              Shown to clients on the marketplace and on task details. A clear, friendly headshot
              builds trust and helps you get claimed faster. New photos are reviewed by an admin
              before going live.
            </p>
            <div className="mt-5 flex items-center gap-5">
              <AgentAvatar src={pendingPhoto || currentPhoto} name={user?.name} size={96} testid="settings-photo-preview" />
              <div className="flex flex-col gap-2">
                <label className="eb-btn eb-btn-primary cursor-pointer" data-testid="settings-photo-upload-label">
                  {busy.photo
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : (currentPhoto ? <Camera className="w-4 h-4" /> : <Upload className="w-4 h-4" />)}
                  {currentPhoto ? "Change photo" : "Upload photo"}
                  <input
                    data-testid="settings-photo-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    disabled={busy.photo}
                    onChange={(e) => uploadPhoto(e.target.files?.[0])}
                  />
                </label>
                <div className="text-[11px] text-[var(--eb-text-dim)]">JPG / PNG / WEBP · up to 5 MB</div>
              </div>
            </div>
            {photoStatus === "pending" && (
              <div
                data-testid="settings-photo-status-pending"
                className="mt-5 inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-[var(--eb-secondary)]/10 text-[var(--eb-secondary)] border border-[var(--eb-secondary)]/30"
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Your new photo is awaiting admin review.
              </div>
            )}
            {photoStatus === "rejected" && (
              <div
                data-testid="settings-photo-status-rejected"
                className="mt-5 text-xs px-3 py-2 rounded-md bg-[var(--eb-danger)]/10 text-[var(--eb-danger)] border border-[var(--eb-danger)]/30"
              >
                Last upload was rejected by an admin. {photoRejectReason ? `Reason: ${photoRejectReason}.` : ""} Please upload a clearer headshot.
              </div>
            )}
            {photoStatus === "approved" && currentPhoto && (
              <div
                data-testid="settings-photo-status-approved"
                className="mt-5 inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-[var(--eb-primary)]/10 text-[var(--eb-primary-deep)] border border-[var(--eb-primary)]/30"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Photo approved — live on the marketplace.
              </div>
            )}
          </section>
        )}

        {/* Password */}
        {user?.auth_provider !== "google" && (
          <section className="eb-card p-7">
            <h2 className="text-xl font-[800]">Change password</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <div>
                <label className="eb-label">Current password</label>
                <input data-testid="settings-old-pw" type="password" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} className="eb-input" />
              </div>
              <div>
                <label className="eb-label">New password</label>
                <input data-testid="settings-new-pw" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="eb-input" />
              </div>
            </div>
            <div className="mt-5 flex justify-end">
              <button data-testid="settings-save-pw" onClick={savePassword} disabled={busy.pw} className="eb-btn eb-btn-primary">
                {busy.pw ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Update password
              </button>
            </div>
          </section>
        )}

        {/* 2FA */}
        <section className="eb-card p-7">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-xl font-[800]">Email two-factor authentication</h2>
              <p className="text-sm text-[var(--eb-text-dim)] mt-1 max-w-md">
                We&apos;ll email you a 6-digit code at sign-in. Recommended for anyone managing higher-value tasks or supervising professional hires.
              </p>
            </div>
            <button
              data-testid="settings-2fa-toggle"
              onClick={toggle2FA}
              disabled={busy.twofa}
              className={user?.two_fa_enabled ? "eb-btn eb-btn-ghost" : "eb-btn eb-btn-primary"}
            >
              {busy.twofa ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {user?.two_fa_enabled ? "Disable 2FA" : "Enable 2FA"}
            </button>
          </div>
          {user?.two_fa_enabled && (
            <div className="mt-4 inline-flex items-center gap-2 text-xs text-[var(--eb-primary-deep)]">
              <ShieldCheck className="w-4 h-4" /> Active — codes sent to {user.email}
            </div>
          )}
        </section>

        {/* Danger */}
        <section className="eb-card p-7">
          <h2 className="text-xl font-[800]">Sign out</h2>
          <p className="text-sm text-[var(--eb-text-dim)] mt-1">End this session on this device.</p>
          <div className="mt-4">
            <button onClick={onLogout} className="eb-btn eb-btn-ghost">Sign out</button>
          </div>
        </section>
      </main>
    </div>
  );
}
